import { useState, useEffect, useMemo } from 'react';
import { purchaseOrderService } from '@/services/purchaseOrderService';
import type {
  PurchaseOrder,
  PurchaseOrderFilters,
  PurchaseOrderFormData,
  PurchaseOrderStats,
  PurchaseOrderState,
  PurchaseOrderActions,
  Supplier,
  PurchaseOrderItem
} from '@/types/purchase-order.types';

// Calculate current week's Monday and Sunday
const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { dateFrom: fmt(monday), dateTo: fmt(sunday) };
};

const weekRange = getWeekRange();

const initialFilters: PurchaseOrderFilters = {
  status: 'all',
  supplier: '',
  location: '',
  ingredient: '',
  dateFrom: weekRange.dateFrom,
  dateTo: weekRange.dateTo,
  sortBy: 'createdAt',
  sortOrder: 'desc'
};

const initialStats: PurchaseOrderStats = {
  total: 0,
  pending: 0,
  approved: 0,
  received: 0,
  cancelled: 0,
  totalAmount: '0.00',
  avgOrderValue: '0.00'
};

export const usePurchaseOrders = (locationId: string, isCajero: boolean = false): PurchaseOrderState & PurchaseOrderActions => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [currentOrder, setCurrentOrder] = useState<PurchaseOrder | null>(null);
  const [stats, setStats] = useState<PurchaseOrderStats>(initialStats);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filters, setFilters] = useState<PurchaseOrderFilters>(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState({
    total: 0,
    hasMore: false,
    topSuppliers: [] as Array<{ name: string; count: number; }>
  });

  // Fetch orders with current filters
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Para CAJERO: sin fechas, solo aprobadas, filtrar por su local
      const effectiveFilters = isCajero
        ? { ...filters, dateFrom: '', dateTo: '', status: 'approved', location: locationId }
        : filters;

      const response = await purchaseOrderService.getPurchaseOrders(locationId, effectiveFilters, 500, 0);
      
      if (response.success) {
        setOrders(response.data);
        setStats(response.meta.stats);
        setMeta({
          total: response.meta.total,
          hasMore: response.meta.hasMore,
          topSuppliers: response.meta.topSuppliers
        });
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar órdenes de compra');
      console.error('Error fetching purchase orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch a specific order
  const fetchOrder = async (orderId: string): Promise<PurchaseOrder | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await purchaseOrderService.getPurchaseOrder(locationId, orderId);
      
      if (response.success) {
        setCurrentOrder(response.data);
        return response.data;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Error al cargar orden de compra');
      console.error('Error fetching purchase order:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Create a new purchase order
  const createOrder = async (orderData: PurchaseOrderFormData): Promise<PurchaseOrder | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await purchaseOrderService.createPurchaseOrder(locationId, orderData);
      
      if (response.success) {
        // Refresh orders list
        await fetchOrders();
        return response.data;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Error al crear orden de compra');
      console.error('Error creating purchase order:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Approve an order
  const approveOrder = async (orderId: string, notes?: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await purchaseOrderService.approvePurchaseOrder(locationId, orderId, notes);
      
      if (response.success) {
        // Update local state
        setOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, ...response.data } : order
        ));
        
        if (currentOrder?.id === orderId) {
          setCurrentOrder(response.data);
        }
        
        return true;
      }
      return false;
    } catch (err: any) {
      setError(err.message || 'Error al aprobar orden de compra');
      console.error('Error approving purchase order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Receive an order
  const receiveOrder = async (
    orderId: string, 
    receivedItems?: PurchaseOrderItem[], 
    notes?: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await purchaseOrderService.receivePurchaseOrder(
        locationId, 
        orderId, 
        receivedItems, 
        notes
      );
      
      if (response.success) {
        // Update local state
        setOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, ...response.data } : order
        ));
        
        if (currentOrder?.id === orderId) {
          setCurrentOrder(response.data);
        }
        
        return true;
      }
      return false;
    } catch (err: any) {
      setError(err.message || 'Error al marcar orden como recibida');
      console.error('Error receiving purchase order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Cancel an order
  const cancelOrder = async (orderId: string, reason?: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await purchaseOrderService.cancelPurchaseOrder(locationId, orderId, reason);
      
      if (response.success) {
        // Update local state
        setOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, ...response.data } : order
        ));
        
        if (currentOrder?.id === orderId) {
          setCurrentOrder(response.data);
        }
        
        return true;
      }
      return false;
    } catch (err: any) {
      setError(err.message || 'Error al cancelar orden de compra');
      console.error('Error cancelling purchase order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Create multiple purchase orders grouped by location
  const createOrdersByLocations = async (
    items: Array<{
      locationId: string;
      locationName: string;
      supplierId: string;
      supplierName: string;
      ingredientId: string;
      ingredientName: string;
      quantity: number;
      unit: string;
      unitCost: number;
      notes?: string;
    }>,
    expectedDeliveryDate?: string,
    notes?: string
  ): Promise<{ orders: PurchaseOrder[]; totalOrders: number; errors?: string[] } | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await purchaseOrderService.createPurchaseOrdersByLocations(
        items,
        expectedDeliveryDate,
        notes
      );

      if (response.success) {
        // Refresh orders list
        await fetchOrders();
        return response.data;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Error al crear órdenes de compra');
      console.error('Error creating purchase orders by locations:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update purchase order
  const updateOrder = async (
    orderId: string,
    data: {
      items?: Array<{
        id: string;
        quantity: number;
        unitCost: number;
        totalCost: number;
      }>;
      subtotal?: number;
      tax?: number;
      total?: number;
      notes?: string;
    }
  ): Promise<PurchaseOrder | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await purchaseOrderService.updatePurchaseOrder(locationId, orderId, data);

      if (response.success) {
        // Refresh orders list
        await fetchOrders();
        return response.data;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Error al actualizar orden de compra');
      console.error('Error updating purchase order:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update filters
  const updateFilters = (newFilters: Partial<PurchaseOrderFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await purchaseOrderService.getSuppliers(locationId);
      if (response.success) {
        setSuppliers(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching suppliers:', err);
    }
  };

  // Load initial data and refresh when location or filters change
  useEffect(() => {
    if (locationId) {
      fetchOrders();
      fetchSuppliers();
    }
  }, [locationId, filters.status]);

  // Filter orders locally (for performance)
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Additional client-side filtering can be added here if needed
    // The main filtering is done on the server side

    return result;
  }, [orders, filters]);

  return {
    // State
    orders: filteredOrders,
    currentOrder,
    stats,
    suppliers,
    filters,
    loading,
    error,
    meta,
    
    // Actions
    fetchOrders,
    fetchOrder,
    createOrder,
    createOrdersByLocations,
    updateOrder,
    approveOrder,
    receiveOrder,
    cancelOrder,
    updateFilters,
    fetchSuppliers
  };
};

export default usePurchaseOrders;